import { INavLink, INavLinkGroup, Nav, Stack } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Shimmer } from "sprint-common-web";

import {
    adminAirlinesRoute,
    adminDistributorsListingRoute,
    adminEncodeSpecificationRoute,
    adminFramerateProfilesListingRoute,
    adminIntegratorsRoute,
    adminLanguageSpecificationsListingRoute,
    adminLanguagesRoute,
    adminManzanitaPresetsListingRoute,
    adminMuxSpecificationListingRoute,
    adminOriginatorsRoute,
    adminResolutionsListingRoute,
    adminTEMRoute,
    adminUsersADFSListingRoute
} from "../../../../admin/AdminRoutes";
import { tvSeriesAssetsSearchRoute } from "../../../../assets/AssetsRoutes";
import {
    ciCloudFileListingRoute,
    ciDeliveryBucketListingRoute,
    ciUploadRoute,
    ciBucketsArchiveListingRoute,
    ciFileRecordsRoute
} from "../../../../content-ingestion/ContentIngestionRoutes";
import {
    assetsManageAncillaryAdvertListingRoute,
    assetsManageAncillaryIndentWarningListingRoute,
    assetsManageAncillaryPromoListingRoute,
    assetsManageAncillarySafetyListingRoute,
    assetsManageDocumentaryListingRoute,
    assetsManageMovieListingRoute,
    assetsManageShortFilmsListingRoute,
    assetsManageTvSeriesListingRoute,
    assetsManageTvSingleListingRoute
} from "../../../../content/ContentRoutes";
import { useFeatureFlags } from "../../../../core/configuration/feature-flags/useFeatureFlags";
import { RouteItem } from "../../../../core/routing/ProtectedRoute";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { User } from "../../../../core/security/User";
import { UserContext } from "../../../../core/security/UserProvider";
import { onPremOrderListingRoute, orderListingRoute, requestListingRoute } from "../../../../orders/OrdersRoutes";
import { materialOrderReportRoute } from "../../../../reports/ReportsRoutes";

export const navigationWidth = 250;

interface NavItem {
    name: string;
    routeItem?: RouteItem;
    icon?: string;
    links?: NavItem[];
    isExpanded?: boolean;
    key: string;
}

const contentIngestioNavItem: NavItem = {
    name: "Content Ingestion",
    links: [
        { name: "Upload", routeItem: ciUploadRoute, icon: "CloudUpload", key: "content-ingestion/upload" },
        {
            name: "Buckets",
            routeItem: ciDeliveryBucketListingRoute,
            icon: "ProductVariant",
            key: "content-ingestion/buckets"
        },
        {
            name: "Archive Buckets",
            routeItem: ciBucketsArchiveListingRoute,
            icon: "ProductVariant",
            key: "content-ingestion/buckets-archive"
        },
        { name: "Ingest", routeItem: ciCloudFileListingRoute, icon: "List", key: "content-ingestion/cloud-files" },
        {
            name: "File Records",
            routeItem: ciFileRecordsRoute,
            icon: "ReservationOrders",
            key: "content-ingestion/file-records"
        }
    ],
    key: "content-ingestion"
};

const assetsNavItem: NavItem = {
    name: "Assets",
    links: [{ name: "Search", routeItem: tvSeriesAssetsSearchRoute, icon: "SearchData", key: "assets/search" }],
    key: "assets"
};

const contentNavItem: NavItem = {
    name: "Content",
    links: [
        { name: "Movies", routeItem: assetsManageMovieListingRoute, icon: "MyMoviesTV", key: "content/movies" },
        {
            name: "TV Singles",
            routeItem: assetsManageTvSingleListingRoute,
            icon: "TVMonitorSelected",
            key: "content/tv-singles"
        },
        {
            name: "Feature Documentaries",
            routeItem: assetsManageDocumentaryListingRoute,
            icon: "Cat",
            key: "content/feature-documentaries"
        },
        {
            name: "Short Films",
            routeItem: assetsManageShortFilmsListingRoute,
            icon: "PanoIndicator",
            key: "content/short-films"
        },
        {
            name: "TV Series",
            routeItem: assetsManageTvSeriesListingRoute,
            icon: "TVMonitor",
            key: "content/tv-series"
        },
        {
            name: "Promos",
            routeItem: assetsManageAncillaryPromoListingRoute,
            icon: "Starburst",
            key: "content/promos"
        },
        {
            name: "Safeties",
            routeItem: assetsManageAncillarySafetyListingRoute,
            icon: "ConstructionCone",
            key: "content/safeties"
        },
        {
            name: "Adverts",
            routeItem: assetsManageAncillaryAdvertListingRoute,
            icon: "Savings",
            key: "content/adverts"
        },
        {
            name: "Indent Warnings",
            routeItem: assetsManageAncillaryIndentWarningListingRoute,
            icon: "OEM",
            key: "content/indentwarnings"
        }
    ],
    key: "content"
};

const adminNavItem: NavItem = {
    name: "Admin",
    links: [
        { name: "Languages", routeItem: adminLanguagesRoute, icon: "LocaleLanguage", key: "admin/languages" },
        { name: "Airlines", routeItem: adminAirlinesRoute, icon: "Group", key: "admin/airlines" },
        {
            name: "Integrators",
            routeItem: adminIntegratorsRoute,
            icon: "WebAppBuilderFragment",
            key: "admin/integrators"
        },
        {
            name: "Encode Specifications",
            routeItem: adminEncodeSpecificationRoute,
            icon: "Processing",
            key: "admin/encode-specifications"
        },
        {
            name: "Mux Specifications",
            routeItem: adminMuxSpecificationListingRoute,
            icon: "ServerProcesses",
            key: "admin/mux-specifications"
        },
        {
            name: "Presets",
            routeItem: adminManzanitaPresetsListingRoute,
            icon: "Preview",
            key: "admin/manzanita-presets"
        },
        { name: "Originators", routeItem: adminOriginatorsRoute, icon: "AlignRight", key: "admin/originators" },
        {
            name: "Distributors",
            routeItem: adminDistributorsListingRoute,
            icon: "DeliveryTruck",
            key: "admin/distributors"
        },
        {
            name: "Framerate Profiles",
            routeItem: adminFramerateProfilesListingRoute,
            icon: "Photo",
            key: "admin/framerate-profiles"
        },
        { name: "Resolutions", routeItem: adminResolutionsListingRoute, icon: "AspectRatio", key: "admin/resolutions" },
        {
            name: "Language Specifications",
            routeItem: adminLanguageSpecificationsListingRoute,
            icon: "ReopenPages",
            key: "admin/language-specifications"
        },
        { name: "Users", routeItem: adminUsersADFSListingRoute, icon: "Group", key: "admin/users" },
        { name: "TEM", routeItem: adminTEMRoute, icon: "AssessmentGroupTemplate", key: "admin/tem" }
    ],
    key: "admin"
};

const fullOrdersNavItem: NavItem = {
    name: "Orders",
    links: [
        { name: "Requests", routeItem: requestListingRoute, icon: "FileComment", key: "orders/requests" },
        { name: "Cloud", routeItem: orderListingRoute, icon: "ReservationOrders", key: "orders/orders" },
        { name: "On-Prem", routeItem: onPremOrderListingRoute, icon: "ReservationOrders", key: "orders/on-prem-orders" }
    ],
    key: "orders"
};

const reportsNavItem: NavItem = {
    name: "Reports",
    links: [
        {
            name: "Materials Dashboard",
            routeItem: materialOrderReportRoute,
            icon: "ReportDocument",
            key: "reports/material-order-report"
        }
    ],
    key: "reports"
};

const hasRole = (user: User, requiredAccessRights: undefined | SprintAccessRight[]): boolean => {
    if (!requiredAccessRights) {
        return false;
    }

    return requiredAccessRights.some((role: SprintAccessRight) => user.accessRights.includes(role));
};

export const SideNavigation = (): JSX.Element => {
    const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
    const userContext = useContext(UserContext);
    const location = useLocation();
    const { isLoading } = useFeatureFlags();

    useEffect(() => {
        if (!location.pathname || location.pathname === "/") {
            setSelectedKey(undefined);

            return;
        }

        const allNavItems = getNavLinkGroups()
            .flatMap((group) => group.links)
            .map((link) => link.links)
            .flat();

        const [, firstSegment, secondSegement] = location.pathname.split("/");
        const keyCandidate = `${firstSegment}/${secondSegement}`;

        const foundNavItem = allNavItems.find((item) => item?.key && keyCandidate === item.key);

        setSelectedKey(foundNavItem?.key);
    }, [location.pathname]);

    const getFilteredNavItem = (navItem: NavItem): NavItem | null => {
        const filteredNavItems: NavItem[] = [];

        if (navItem.links) {
            navItem.links.forEach((subItem: NavItem) => {
                const filteredNavItem = getFilteredNavItem(subItem);
                if (filteredNavItem) {
                    filteredNavItems.push(filteredNavItem);
                }
            });

            if (filteredNavItems.length > 0) {
                navItem.links = filteredNavItems;

                return navItem;
            }

            return null;
        }

        if (hasRole(userContext, navItem.routeItem?.accessRights)) {
            return navItem;
        }

        return null;
    };

    const getNavLinkGroups = (): INavLinkGroup[] => {
        const navItems = createFiltredNavigation();
        return [
            {
                links: navItems.map(getNavLink)
            }
        ];
    };

    const getTestId = (name: string) => {
        return `${name.trim().replace(" ", "-").toLocaleLowerCase()}-navigation-item`;
    };

    const getNavLink = (navItem: NavItem): INavLink => {
        const url = getUrl(navItem.routeItem?.path);
        const locationFirstSegment = location.pathname.split("/")[1];

        const shouldBeExpanded = locationFirstSegment === navItem.key && !!navItem.links?.length;

        return {
            key: navItem.key,
            isExpanded: shouldBeExpanded,
            name: navItem.name,
            icon: navItem.icon,
            url,
            "data-testid": getTestId(navItem.name),
            links: navItem.links
                ? navItem.links.map((item: NavItem) => {
                      return getNavLink(item);
                  })
                : undefined
        };
    };

    const getUrl = (path: string | undefined) => {
        if (path) {
            return `${process.env.PUBLIC_URL}/#${path}`;
        }

        return "";
    };

    const createFiltredNavigation = (): NavItem[] => {
        const MenuItems: NavItem[] = [
            contentIngestioNavItem,
            assetsNavItem,
            contentNavItem,
            adminNavItem,
            fullOrdersNavItem,
            reportsNavItem
        ];

        const menu: NavItem = { links: MenuItems, name: "Main navigation", key: "main" };
        const filteredMenu = getFilteredNavItem(menu);

        return filteredMenu?.links ?? [];
    };

    return (
        <Stack horizontal={true} data-testid="navigation">
            {isLoading ? (
                <Stack
                    styles={{ root: { width: 250, display: "flex", flexDirection: "column", gap: 12, padding: 10 } }}
                >
                    <Shimmer width={"60%"} styles={{ shimmerWrapper: { padding: 4 } }} />
                    <Shimmer width={"40%"} styles={{ shimmerWrapper: { padding: 4 } }} />
                    <Shimmer width={"50%"} styles={{ shimmerWrapper: { padding: 4 } }} />
                    <Shimmer width={"40%"} styles={{ shimmerWrapper: { padding: 4 } }} />
                    <Shimmer width={"40%"} styles={{ shimmerWrapper: { padding: 4 } }} />
                </Stack>
            ) : (
                <Nav
                    groups={getNavLinkGroups()}
                    styles={{ root: { width: navigationWidth } }}
                    selectedKey={selectedKey}
                />
            )}
        </Stack>
    );
};
